/**
 * Each language in its own name, which is the name a reader of that language
 * finds in a list of languages they cannot otherwise read. Keyed by ISO 639
 * code; a language joins this table before any surface offers it, so every
 * surface that lists it (the guest portal's switcher, the host app's picker)
 * spells it the same way.
 */
export const LANGUAGE_ENDONYMS = {
  de: "Deutsch",
  en: "English",
  fr: "Français",
  it: "Italiano",
  sl: "Slovenščina",
} as const satisfies Record<string, string>;

/** A language whose own name {@link LANGUAGE_ENDONYMS} carries. */
export type EndonymLanguage = keyof typeof LANGUAGE_ENDONYMS;

/**
 * Languages sorted by their own names, the order a reader scans a list of
 * languages for theirs. Collated as English on every device: an unsupported
 * tag such as `und` falls back to the runtime's default locale, which would
 * let the order change with the device.
 */
export function byEndonym<L extends EndonymLanguage>(
  languages: readonly L[]
): L[] {
  return [...languages].sort((a, b) =>
    LANGUAGE_ENDONYMS[a].localeCompare(LANGUAGE_ENDONYMS[b], "en")
  );
}

/**
 * The languages the host app's own words are written in. English comes first:
 * every other catalog translates it key for key, and it is the one a reader
 * lands on when nothing says which language they read.
 */
export const HOST_LOCALES = [
  "en",
  "de",
  "fr",
  "it",
  "sl",
] as const satisfies readonly EndonymLanguage[];

export type HostLocale = (typeof HOST_LOCALES)[number];

/** The language the host app speaks when no signal names one it speaks. */
export const DEFAULT_HOST_LOCALE: HostLocale = "en";

/**
 * The locale dates and numbers are written in, per host language. English
 * follows the European conventions the app's hosts live by ("Sun 22 Nov",
 * "17:00", "€1,155.00").
 */
export const HOST_FORMAT_LOCALE: Record<HostLocale, string> = {
  de: "de",
  en: "en-GB",
  fr: "fr",
  it: "it",
  sl: "sl",
};

/**
 * The host locale a BCP-47 tag belongs to, by its language subtag
 * (`EN-gb` → `en`, `sl-SI` → `sl`), or null when the tag is malformed, names
 * no language, or names one the app does not speak.
 */
export function hostLocaleOf(
  tag: string | null | undefined
): HostLocale | null {
  if (!tag) {
    return null;
  }
  try {
    const { language } = new Intl.Locale(tag.trim());
    return HOST_LOCALES.find((locale) => locale === language) ?? null;
  } catch {
    // Not a well-formed tag: a hand-edited setting, an odd device report.
    return null;
  }
}

/** The host locale of the first of `tags` that names one, or null. */
export function firstHostLocale(
  tags: readonly (string | null | undefined)[]
): HostLocale | null {
  for (const tag of tags) {
    const locale = hostLocaleOf(tag);
    if (locale) {
      return locale;
    }
  }
  return null;
}
